import {Box} from '@dropbox/dig-foundations';
import {getPlanningBreakoutStyle} from 'components/DSYS/editor/planningTableLayout';
import {usePlanningTableReserveRight} from 'components/DSYS/editor/PlanningTableReserveContext';
import {BUTTON_SIZE} from 'components/DSYS/editor/plugins/tableButtons/utils';
import {PLANNING_COMMENT_ORIGIN_ATTR} from 'components/DSYS/editor/utils/planningCommentOrigin';
import type {LexicalEditor} from 'lexical';
import type {CSSProperties, ReactNode} from 'react';
import {useLayoutEffect, useState} from 'react';

import wideTableShellStyles from './WideTableShell.module.css';

/** Sized to columns (`max-content`); centered in `.wideRoot` flex shell so resize stays centered. */
export const PLANNING_TABLE_SHELL_STYLE: CSSProperties = {
  flexShrink: 0,
  maxWidth: '100%',
  width: 'max-content',
};

export function usePlanningEditorWideBreakoutStyle({
  editor,
  tableWidthPx,
  fillsContainer = false,
}: {
  editor: LexicalEditor;
  tableWidthPx: number;
  /**
   * DSYS planning tables that stretch to the breakout width can under-report
   * `tableWidthPx` from column sums; pass true so reserve math uses full strip width.
   */
  fillsContainer?: boolean;
}): CSSProperties | undefined {
  const [style, setStyle] = useState<CSSProperties | undefined>(undefined);
  const desiredReserveRightPx = usePlanningTableReserveRight();

  useLayoutEffect(() => {
    const update = () => {
      const editorElement = editor.getRootElement();
      if (!editorElement) {
        setStyle(undefined);
        return;
      }
      const editorRect = editorElement.getBoundingClientRect();
      const buttonOffset = BUTTON_SIZE + 1;
      const {widthPx, marginLeftPx} = getPlanningBreakoutStyle({
        windowWidth: window.innerWidth,
        editorWidth: editorRect.width,
        buttonOffset,
        desiredReserveRightPx,
        tableWidthPx,
        fillsContainer,
      });
      const handleBleedPx = BUTTON_SIZE;
      setStyle({
        width: `${widthPx}px`,
        marginLeft: `${marginLeftPx - handleBleedPx}px`,
        marginRight: `${-handleBleedPx}px`,
        maxWidth: 'none',
      });
    };

    update();
    const raf = requestAnimationFrame(() => update());
    window.addEventListener('resize', update);
    const ro = new ResizeObserver(() => update());
    const editorEl = editor.getRootElement();
    if (editorEl) {
      ro.observe(editorEl);
    }
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', update);
      ro.disconnect();
    };
  }, [editor, fillsContainer, tableWidthPx, desiredReserveRightPx]);

  return style;
}

export const PlanningTableWideShell = ({
  children,
  editor,
  tableWidthPx,
  fillsContainer = false,
  commentOriginId,
}: {
  children: ReactNode;
  editor: LexicalEditor;
  tableWidthPx: number;
  fillsContainer?: boolean;
  commentOriginId?: string;
}) => {
  const wideStyle = usePlanningEditorWideBreakoutStyle({editor, fillsContainer, tableWidthPx});
  return (
    <Box
      className={wideTableShellStyles.wideBreakout}
      data-planning-wide-table
      {...(commentOriginId ? {[PLANNING_COMMENT_ORIGIN_ATTR]: commentOriginId} : {})}
      style={wideStyle}
    >
      <Box className={wideTableShellStyles.wideRoot} data-planning-dynamic-table maxWidth="100%">
        {children}
      </Box>
    </Box>
  );
};
